/**
 * DisclosuresContext — Risk Mgmt Panel.
 *
 * Tier-3 Disclosure Engine M4 — read-side config consumer for the authoring
 * workbench. Mirrors ROP-teams `ConfigContext`'s disclosures envelope shape
 * (templates / versions / sections / variants / merge field groups /
 * definitions) but scoped to just this domain — the panel doesn't need the
 * other 21 config slices ROP-teams pulls.
 *
 * Loads on mount via inline `getDisclosuresConfig()` (route already exists
 * today — M1 deliverable). M3 will add CRUD; this context will gain mutate
 * helpers + optimistic refresh at that point.
 *
 * Cache: in-memory only, refresh on demand. No TTL — admin workbench surfaces
 * are short-lived sessions and refresh is one click.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import {
  getDisclosuresConfig,
  type DisclosuresEnvelope,
} from '@/services/disclosuresApi';
import {
  getDisclosureOptions,
  type DisclosureOptions,
} from '@/services/disclosuresAdminApi';

const EMPTY_ENVELOPE: DisclosuresEnvelope = {
  templates: [],
  versions: [],
  sections: [],
  variants: [],
  mergeFieldGroups: [],
  mergeFieldDefinitions: [],
  workflowLinks: [],
};

export type DisclosuresLoadState = 'idle' | 'loading' | 'ok' | 'error';

export interface DisclosuresContextValue {
  envelope: DisclosuresEnvelope;
  state: DisclosuresLoadState;
  error: string | null;
  lastLoadedAt: Date | null;
  refresh: () => Promise<void>;
  /** Closed value sets + live catalogs for the pickers (GET /v3/admin/disclosures/options). */
  options: DisclosureOptions | null;
  optionsError: string | null;
}

const DisclosuresContext = createContext<DisclosuresContextValue | null>(null);

export function DisclosuresProvider({
  children,
}: {
  children: ReactNode;
}): JSX.Element {
  const [envelope, setEnvelope] = useState<DisclosuresEnvelope>(EMPTY_ENVELOPE);
  const [state, setState] = useState<DisclosuresLoadState>('idle');
  const [error, setError] = useState<string | null>(null);
  const [lastLoadedAt, setLastLoadedAt] = useState<Date | null>(null);
  const inflight = useRef<AbortController | null>(null);
  const [options, setOptions] = useState<DisclosureOptions | null>(null);
  const [optionsError, setOptionsError] = useState<string | null>(null);

  const loadOptions = useCallback(async (): Promise<void> => {
    const r = await getDisclosureOptions();
    if (r.success && r.data) {
      setOptions(r.data);
      setOptionsError(null);
    } else {
      setOptionsError(r.error ?? 'Could not load the pick lists.');
    }
  }, []);

  const refresh = useCallback(async (): Promise<void> => {
    inflight.current?.abort();
    const controller = new AbortController();
    inflight.current = controller;
    setState('loading');
    setError(null);
    try {
      const resp = await getDisclosuresConfig(controller.signal);
      if (controller.signal.aborted) return;
      if (!resp.success) {
        setEnvelope(EMPTY_ENVELOPE);
        setState('error');
        setError(resp.error ?? 'Failed to load disclosures config.');
        return;
      }
      setEnvelope(resp.envelope);
      setState('ok');
      setLastLoadedAt(new Date());
    } catch (err) {
      if (controller.signal.aborted) return;
      setEnvelope(EMPTY_ENVELOPE);
      setState('error');
      setError(err instanceof Error ? err.message : 'Failed to load.');
    }
  }, []);

  useEffect(() => {
    void refresh();
    void loadOptions();
    return () => inflight.current?.abort();
  }, [refresh, loadOptions]);

  const value = useMemo<DisclosuresContextValue>(
    () => ({ envelope, state, error, lastLoadedAt, refresh, options, optionsError }),
    [envelope, state, error, lastLoadedAt, refresh, options, optionsError],
  );

  return (
    <DisclosuresContext.Provider value={value}>
      {children}
    </DisclosuresContext.Provider>
  );
}

export function useDisclosuresConfig(): DisclosuresContextValue {
  const ctx = useContext(DisclosuresContext);
  if (!ctx) {
    throw new Error(
      'useDisclosuresConfig must be used within DisclosuresProvider',
    );
  }
  return ctx;
}
